<template>
  <div class="farm-order">
    <el-card class="main-card">
      <div class="header-section">
        <h2 class="page-title">
          <i class="el-icon-s-order title-icon"></i>
          农场订单管理
        </h2>
      </div>
      
      <div class="content-section">
        <!-- 搜索栏 -->
        <div class="search-box">
          <el-row type="flex" :gutter="12" class="search-row">
            <el-col :span="6">
              <el-row type="flex" :gutter="12">
                <el-col :span="24">
                  <el-input
                    size="small"
                    clearable
                    placeholder="请输入订单编号"
                    prefix-icon="el-icon-document"
                    v-model="query.order_no"
                    @keyup.enter.native="getList"
                    class="search-input"
                  />
                </el-col>
              </el-row>
              <el-row type="flex" :gutter="12" style="margin-top: 12px;">
                <el-col :span="24">
                  <el-input
                    size="small"
                    clearable
                    placeholder="请输入收货人姓名"
                    prefix-icon="el-icon-user"
                    v-model="query.consignee_name"
                    @keyup.enter.native="getList"
                    class="search-input"
                  />
                </el-col>
              </el-row>
              <el-row type="flex" :gutter="12" style="margin-top: 12px;">
                <el-col :span="24">
                  <el-input
                    size="small"
                    clearable
                    placeholder="请输入联系电话"
                    prefix-icon="el-icon-phone"
                    v-model="query.consignee_phone"
                    @keyup.enter.native="getList"
                    class="search-input"
                  />
                </el-col>
              </el-row>
            </el-col>
            <el-col :span="18">
              <div class="search-buttons search-buttons-right">
                <el-button type="primary" size="small" @click="getList">查询</el-button>
                <el-button size="small" @click="resetQuery">重置</el-button>
                <el-button 
                  size="small" 
                  @click="selectAllPendingShip"
                  :disabled="pendingShipOrders.length === 0"
                >
                  全选
                </el-button>
                <el-button 
                  size="small" 
                  @click="clearSelection"
                  :disabled="selectedOrders.length === 0"
                >
                  取消选择
                </el-button>
                <el-button 
                  type="primary" 
                  size="small" 
                  @click="openBatchShipDialog"
                  :disabled="selectedOrders.length === 0"
                >
                  批量发货 ({{ selectedOrders.length }})
                </el-button>
              </div>
            </el-col>
          </el-row>
          
          <!-- 状态筛选 -->
          <div class="filter-section">
            <span class="filter-label">订单状态：</span>
            <el-radio-group v-model="query.status" size="small" @change="handleStatusChange" class="filter-radio">
              <el-radio-button label="">全部</el-radio-button>
              <el-radio-button label="0">待付款</el-radio-button>
              <el-radio-button label="1">待发货</el-radio-button>
              <el-radio-button label="2">待收货</el-radio-button>
              <el-radio-button label="3">已完成</el-radio-button>
              <el-radio-button label="4">已取消</el-radio-button>
            </el-radio-group>
          </div>
        </div>
        
        <!-- 订单列表 -->
        <div class="table-wrapper">
          <div class="table-container" v-loading="tableLoading">
            <div v-for="order in orderList" :key="order.id" class="order-card">
              <div class="order-card-header">
                <div class="order-info-left">
                  <el-checkbox 
                    v-if="order.status === 1" 
                    v-model="selectedOrders" 
                    :label="order.id"
                    class="order-checkbox"
                  ></el-checkbox>
                  <span class="order-no-label">订单编号：</span>
                  <span class="order-no-value">{{ order.order_no }}</span>
                </div>
                <div class="order-info-right">
                  <span class="order-status-badge">
                    <el-tag v-if="order.status === 0" size="small" type="warning">待付款</el-tag>
                    <el-tag v-else-if="order.status === 1" size="small" type="primary">待发货</el-tag>
                    <el-tag v-else-if="order.status === 2" size="small" type="success">待收货</el-tag>
                    <el-tag v-else-if="order.status === 3" size="small">已完成</el-tag>
                    <el-tag v-else-if="order.status === 4" size="small" type="info">已取消</el-tag>
                  </span>
                  <span class="create-time">{{ formatDate(order.create_time) }}</span>
                </div>
              </div>
              
              <div class="order-card-body">
                <!-- 收货信息 -->
                <div class="address-section">
                  <i class="el-icon-location-outline address-icon"></i>
                  <span class="address-info">
                    <span class="consignee-name">{{ getConsigneeName(order) }}</span>
                    <span class="consignee-phone">{{ getConsigneePhone(order) }}</span>
                    <span class="address-detail">{{ getAddressDetail(order) }}</span>
                  </span>
                </div>
                
                <!-- 商品列表 -->
                <el-table :data="order.goods_list" style="width: 100%" size="small" :show-header="false" class="goods-table">
                  <el-table-column width="80" align="center">
                    <template v-slot="scope">
                      <img 
                        v-if="scope.row.image || scope.row.goods_image || scope.row.thumb" 
                        :src="scope.row.image || scope.row.goods_image || scope.row.thumb" 
                        class="goods-image"
                        @error="handleImageError"
                      />
                      <div v-else class="no-image">
                        <i class="el-icon-picture"></i>
                      </div>
                    </template>
                  </el-table-column>
                  <el-table-column prop="goods_name" label="商品名称" min-width="200">
                    <template v-slot="scope">
                      <div class="goods-name-cell">{{ scope.row.goods_name }}</div>
                    </template>
                  </el-table-column>
                  <el-table-column prop="price" label="单价" width="100">
                    <template v-slot="scope">
                      <span class="price-cell">¥{{ scope.row.price }}</span>
                    </template>
                  </el-table-column>
                  <el-table-column prop="num" label="数量" width="80" align="center">
                    <template v-slot="scope">
                      <span class="quantity-cell">× {{ scope.row.quantity }}</span>
                    </template>
                  </el-table-column>
                  <el-table-column label="小计" width="100">
                    <template v-slot="scope">
                      <span class="subtotal-cell">¥{{ (scope.row.price * scope.row.quantity).toFixed(2) }}</span>
                    </template>
                  </el-table-column>
                </el-table>
              </div>
              
              <div class="order-card-footer">
                <div class="order-summary">
                  <span class="summary-text">
                    <i class="el-icon-goods"></i>
                    共 <span class="highlight-text">{{ getTotalGoodsNum(order) }}</span> 件商品
                  </span>
                  <span class="total-amount">
                    订单合计：
                    <span class="amount-text">¥{{ order.total_amount }}</span>
                  </span>
                </div>
                <div class="order-actions">
                  <el-button
                    v-if="order.status === 0"
                    size="small"
                    type="warning"
                    class="action-btn"
                    @click="cancelOrderHandle(order)"
                  >
                    <i class="el-icon-close"></i>
                    取消订单
                  </el-button>
                  <el-button
                    v-if="order.status === 1"
                    size="small"
                    type="primary"
                    class="action-btn primary-btn"
                    @click="deliverOrder(order)"
                  >
                    <i class="el-icon-truck"></i>
                    立即发货
                  </el-button>
                  <el-button
                    v-if="order.status === 2"
                    size="small"
                    type="success"
                    class="action-btn"
                    @click="confirmReceiveHandle(order)"
                  >
                    <i class="el-icon-check"></i>
                    完成订单
                  </el-button>
                  <el-button
                    v-if="[3, 4].includes(order.status)"
                    size="small"
                    type="danger"
                    class="action-btn"
                    @click="deleteOrderHandle(order)"
                  >
                    <i class="el-icon-delete"></i>
                    删除订单
                  </el-button>
                  <el-button
                    size="small"
                    icon="el-icon-view"
                    class="action-btn view-btn"
                    @click="openDetail(order)"
                  >
                    详情
                  </el-button>
                </div>
              </div>
            </div>
            
            <!-- 空状态 -->
            <div v-if="orderList.length === 0 && !tableLoading" class="empty-state">
              <i class="el-icon-s-order empty-icon"></i>
              <p class="empty-text">暂无订单数据</p>
            </div>
            
            <!-- 分页 -->
            <el-pagination
              class="pagination-box"
              :pager-count="7"
              @current-change="pageChange"
              @size-change="sizeChange"
              :page-sizes="[30, 60, 100]"
              layout="total, sizes, prev, pager, next, jumper"
              :page-size="query.limit"
              background
              :total="exp.total"
            />
          </div>
        </div>
      </div>
    </el-card>
    
    <!-- 订单详情弹窗 -->
    <el-dialog
      v-dialogDrag
      title="订单详情"
      :visible.sync="detailVisible"
      width="800px"
      class="detail-dialog"
    >
      <div v-if="detailInfo" class="detail-content">
        <el-descriptions :column="2" border size="small" class="detail-table">
          <el-descriptions-item label="订单编号">{{ detailInfo.order_no }}</el-descriptions-item>
          <el-descriptions-item label="订单状态">
            <el-tag v-if="detailInfo.status === 0" size="small" type="warning">待付款</el-tag>
            <el-tag v-else-if="detailInfo.status === 1" size="small" type="primary">待发货</el-tag>
            <el-tag v-else-if="detailInfo.status === 2" size="small" type="success">待收货</el-tag>
            <el-tag v-else-if="detailInfo.status === 3" size="small">已完成</el-tag>
            <el-tag v-else-if="detailInfo.status === 4" size="small" type="info">已取消</el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="收货人">{{ getDetailConsigneeName(detailInfo) }}</el-descriptions-item>
          <el-descriptions-item label="联系电话">{{ getDetailConsigneePhone(detailInfo) }}</el-descriptions-item>
          <el-descriptions-item label="收货地址" :span="2">{{ getDetailAddressDetail(detailInfo) }}</el-descriptions-item>
          <el-descriptions-item label="下单时间">{{ formatDate(detailInfo.create_time) }}</el-descriptions-item>
          <el-descriptions-item label="发货时间" v-if="detailInfo.shipping_time">{{ formatDate(detailInfo.shipping_time) }}</el-descriptions-item>
        </el-descriptions>
        
        <!-- 物流轨迹 -->
        <div v-if="detailInfo.logistics && detailInfo.logistics.traces && detailInfo.logistics.traces.length > 0" class="logistics-section">
          <h3 class="section-title">
            <i class="el-icon-truck"></i>
            物流轨迹
          </h3>
          <div class="logistics-timeline">
            <div v-for="(item, index) in detailInfo.logistics.traces" :key="index" class="logistics-item">
              <div class="logistics-dot" :class="{ 'is-first': index === 0 }"></div>
              <div class="logistics-content">
                <div class="logistics-time">{{ formatLogisticsTime(item.create_time || item.time) }}</div>
                <div class="logistics-desc">{{ item.desc || item.description || item.content || item.remark }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="detailVisible = false">关闭</el-button>
      </div>
    </el-dialog>
    
    <!-- 订单发货弹窗 -->
    <el-dialog
      v-dialogDrag
      title="订单发货"
      :visible.sync="deliverVisible"
      width="500px"
      class="deliver-dialog"
    >
      <el-form :model="deliverForm" label-width="100px" class="deliver-form">
        <el-form-item label="快递公司">
          <el-select v-model="deliverForm.express_company" placeholder="请选择快递公司" style="width: 100%" size="small">
            <el-option label="顺丰速运" value="SF"/>
            <el-option label="中通快递" value="ZTO"/>
            <el-option label="圆通速递" value="YTO"/>
            <el-option label="韵达快递" value="YD"/>
            <el-option label="申通快递" value="STO"/>
            <el-option label="EMS" value="EMS"/>
            <el-option label="其他" value="other"/>
          </el-select>
        </el-form-item>
        <el-form-item label="快递单号">
          <el-input v-model="deliverForm.express_no" placeholder="请输入快递单号" size="small"/>
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="deliverForm.remark" type="textarea" :rows="3" placeholder="选填" size="small"/>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="deliverVisible = false">取消</el-button>
        <el-button type="primary" :loading="btnLoading" @click="confirmDeliver">确认发货</el-button>
      </div>
    </el-dialog>
    
    <!-- 批量发货弹窗 -->
    <el-dialog
      v-dialogDrag
      title="批量发货"
      :visible.sync="batchShipVisible"
      width="600px"
      class="batch-ship-dialog"
    >
      <div class="batch-ship-info">
        <el-alert
          type="info" :closable="false" show-icon>
          已选择 <strong>{{ selectedOrders.length }}</strong> 个待发货订单
        </el-alert>
      </div>
      <el-form :model="batchShipForm" label-width="100px" class="batch-ship-form">
        <el-form-item label="快递公司">
          <el-select v-model="batchShipForm.express_company" placeholder="请选择快递公司" style="width: 100%" size="small">
            <el-option label="顺丰速运" value="SF"/>
            <el-option label="中通快递" value="ZTO"/>
            <el-option label="圆通速递" value="YTO"/>
            <el-option label="韵达快递" value="YD"/>
            <el-option label="申通快递" value="STO"/>
            <el-option label="EMS" value="EMS"/>
            <el-option label="其他" value="other"/>
          </el-select>
        </el-form-item>
        <el-form-item label="快递单号规则">
          <el-input v-model="batchShipForm.express_no" type="textarea" :rows="6" placeholder="请输入快递单号，每个订单一行，格式：订单号,快递单号" size="small"/>
          <div class="form-tip">格式示例：<br>123456789,SF123456789<br>987654321,YT987654321</div>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="batchShipVisible = false">取消</el-button>
        <el-button type="primary" :loading="btnLoading" @click="confirmBatchShip">确认批量发货</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  getOrderList,
  getOrderDetail,
  orderShip,
  orderBatchShip,
  cancelOrder,
  deleteOrder,
  confirmReceive,
  farmOrderFinish,
} from '@/api/laobing_farm/order.js'
import {
  getCurrentShop,
} from '@/api/leifeng_shop_config.js'

export default {
  name: 'LaobingFarmOrderList',
  data() {
    return {
      tableLoading: false,
      exp: { total: 0 },
      btnLoading: false,
      currentShopConfig: null,
      query: {
        page: 1,
        limit: 20,
        order_no: '',
        consignee_name: '',
        consignee_phone: '',
        status: '',
        order_type: 'claim',
      },
      orderList: [],
      detailVisible: false,
      detailInfo: null,
      deliverVisible: false,
      deliverForm: {
        order_id: '',
        express_company: '',
        express_no: '',
        remark: '',
      },
      currentOrder: null,
      selectedOrders: [],
      batchShipVisible: false,
      batchShipForm: {
        express_company: 'SF',
        express_no: '',
      },
    }
  },
  computed: {
    pendingShipOrders() {
      return this.orderList.filter(order => order.status === 1)
    },
    isAllSelected() {
      const pendingIds = this.pendingShipOrders.map(order => order.id)
      return pendingIds.length > 0 && pendingIds.every(id => this.selectedOrders.includes(id))
    },
  },
  created() {
    this.loadCurrentShopConfig()
  },
  methods: {
    async loadCurrentShopConfig() {
      try {
        const { data: res } = await getCurrentShop()
        if (res.code === 200 || res.code === 1 || res.code === 0) {
          this.currentShopConfig = res.data
          this.getList()
        } else {
          this.$message.error(res.message || '获取店铺配置失败')
          this.getList()
        }
      } catch (error) {
        console.error('获取店铺配置错误:', error)
        this.getList()
      }
    },
    async getList() {
      this.tableLoading = true
      try {
        const queryData = { ...this.query }
        // 如果有店铺配置，添加店铺过滤
        if (this.currentShopConfig && this.currentShopConfig.is_all === 0) {
          let shopIds = this.currentShopConfig.shop_ids
          if (typeof shopIds === 'string') {
            shopIds = shopIds.split(',').map(id => parseInt(id))
          }
          if (Array.isArray(shopIds) && shopIds.length > 0) {
            queryData.shop_ids = shopIds
          }
        }
        const { data: res } = await getOrderList(queryData)
        console.log('订单列表完整返回:', res)
        if (res.code === 200 || res.code === 1 || res.code === 0) {
          const listData = res.data || res.list || []
          this.orderList = Array.isArray(listData) ? listData : (listData.data || listData.list || [])
          this.exp.total = res.total || res.count || (res.data && res.data.total) || (listData.total) || this.orderList.length
          console.log('解析后的订单列表:', this.orderList)
          if (this.orderList.length > 0) {
            console.log('第一个订单数据结构:', this.orderList[0])
          }
        } else {
          this.$message.error(res.message || '获取订单列表失败')
        }
      } catch (error) {
        console.error('获取订单列表错误:', error)
        this.$message.error('网络错误，请稍后重试')
      } finally {
        this.tableLoading = false
      }
    },
    pageChange(page) {
      this.query.page = page
      this.getList()
    },
    selectAllPendingShip() {
      this.selectedOrders = this.pendingShipOrders.map(order => order.id)
    },
    clearSelection() {
      this.selectedOrders = []
    },
    sizeChange(size) {
      this.query.limit = size
      this.query.page = 1
      this.getList()
    },
    handleStatusChange() {
      this.query.page = 1
      this.getList()
    },
    resetQuery() {
      this.query = {
        page: 1,
        limit: 20,
        order_no: '',
        consignee_name: '',
        consignee_phone: '',
        status: '',
        order_type: 'claim',
      }
      this.getList()
    },
    formatDate(timestamp) {
      if (!timestamp) return ''
      const date = new Date(timestamp * 1000)
      return date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0') + '-' + String(date.getDate()).padStart(2, '0') + ' ' + String(date.getHours()).padStart(2, '0') + ':' + String(date.getMinutes()).padStart(2, '0') + ':' + String(date.getSeconds()).padStart(2, '0')
    },
    setDefaultImage(event) {
      const img = event.target
      img.style.display = 'none'
      const parent = img.parentElement
      if (parent) {
        const noImageDiv = document.createElement('div')
        noImageDiv.className = 'no-image'
        noImageDiv.innerHTML = '<i class="el-icon-picture"></i>'
        parent.appendChild(noImageDiv)
      }
    },
    handleImageError(event) {
      event.target.style.display = 'none'
    },
    getTotalGoodsNum(order) {
      if (!order.goods_list || !Array.isArray(order.goods_list)) {
        return 0
      }
      return order.goods_list.reduce((total, goods) => {
        return total + (goods.num || goods.count || goods.quantity || 1)
      }, 0)
    },
    getConsigneeName(order) {
      return order.receiver_name || order.consignee || order.consignee_name || order.name || ''
    },
    getConsigneePhone(order) {
      return order.receiver_phone || order.consignee_phone || order.phone || order.mobile || ''
    },
    getAddressDetail(order) {
      const parts = []
      if (order.province) parts.push(order.province)
      if (order.city) parts.push(order.city)
      if (order.district) parts.push(order.district)
      if (order.detail_address) parts.push(order.detail_address)
      if (order.address) parts.push(order.address)
      if (order.detail) parts.push(order.detail)
      return parts.join('') || ''
    },
    getDetailConsigneeName(info) {
      return info.receiver_name || info.consignee || info.consignee_name || info.name || ''
    },
    getDetailConsigneePhone(info) {
      return info.receiver_phone || info.consignee_phone || info.phone || info.mobile || ''
    },
    getDetailAddressDetail(info) {
      const parts = []
      if (info.province) parts.push(info.province)
      if (info.city) parts.push(info.city)
      if (info.district) parts.push(info.district)
      if (info.detail_address) parts.push(info.detail_address)
      if (info.address) parts.push(info.address)
      if (info.detail) parts.push(info.detail)
      return parts.join('') || ''
    },
    formatLogisticsTime(timestamp) {
      if (!timestamp) return ''
      if (typeof timestamp === 'string' && timestamp.length > 10) {
        return timestamp
      }
      const date = new Date(timestamp * 1000)
      return date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0') + '-' + String(date.getDate()).padStart(2, '0') + ' ' + String(date.getHours()).padStart(2, '0') + ':' + String(date.getMinutes()).padStart(2, '0')
    },
    async openDetail(order) {
      try {
        const { data: res } = await getOrderDetail({ order_id: order.id })
        console.log('订单详情完整返回:', res)
        if (res.code === 200 || res.code === 1 || res.code === 0) {
          this.detailInfo = res.data
          console.log('订单详情数据结构:', this.detailInfo)
          this.detailVisible = true
        } else {
          this.$message.error(res.message || '获取订单详情失败')
        }
      } catch (error) {
        console.error('获取订单详情错误:', error)
        this.$message.error('网络错误，请稍后重试')
      }
    },
    deliverOrder(order) {
      this.deliverForm = {
        order_id: order.id,
        express_company: '',
        express_no: '',
        remark: '',
      }
      this.deliverVisible = true
    },
    async confirmDeliver() {
      if (!this.deliverForm.express_company) {
        this.$message.warning('请选择快递公司')
        return
      }
      if (!this.deliverForm.express_no) {
        this.$message.warning('请输入快递单号')
        return
      }
      this.btnLoading = true
      try {
        const { data: res } = await orderShip(this.deliverForm)
        if (res.code === 200 || res.code === 1 || res.code === 0) {
          this.$message.success('发货成功')
          this.deliverVisible = false
          this.getList()
        } else {
          this.$message.error(res.message || '发货失败')
        }
      } catch (error) {
        this.$message.error('网络错误，请稍后重试')
      } finally {
        this.btnLoading = false
      }
    },
    openBatchShipDialog() {
      if (this.selectedOrders.length === 0) {
        this.$message.warning('请先选择要发货的订单')
        return
      }
      this.batchShipForm = {
        express_company: 'SF',
        express_no: '',
      }
      this.batchShipVisible = true
    },
    async confirmBatchShip() {
      if (!this.batchShipForm.express_company) {
        this.$message.warning('请选择快递公司')
        return
      }
      if (!this.batchShipForm.express_no) {
        this.$message.warning('请输入快递单号')
        return
      }
      
      const items = []
      const lines = this.batchShipForm.express_no.split('\n').filter(line => line.trim())
      
      for (const line of lines) {
        const parts = line.split(',').map(p => p.trim())
        if (parts.length >= 2) {
          const orderId = parts[0]
          const expressNo = parts[1]
          
          if (this.selectedOrders.includes(parseInt(orderId))) {
            items.push({
              order_id: parseInt(orderId),
              express_company: this.batchShipForm.express_company,
              express_no: expressNo,
            })
          }
        }
      }
      
      if (items.length === 0) {
        this.$message.warning('没有匹配的订单快递单号')
        return
      }
      
      this.btnLoading = true
      try {
        const { data: res } = await orderBatchShip({ items })
        if (res.code === 200 || res.code === 1 || res.code === 0) {
          let message = res.message || '批量发货成功'
          if (res.data && res.data.fail && res.data.fail.length > 0) {
            message += `，失败：${res.data.fail.length} 单`
            this.$message.warning(message)
            console.log('批量发货失败详情：', res.data.fail)
          } else {
            this.$message.success(message)
          }
          this.batchShipVisible = false
          this.selectedOrders = []
          this.getList()
        } else {
          this.$message.error(res.message || '批量发货失败')
        }
      } catch (error) {
        console.error('批量发货错误:', error)
        this.$message.error('网络错误，请稍后重试')
      } finally {
        this.btnLoading = false
      }
    },
    cancelOrderHandle(order) {
      this.$confirm(`确定要取消订单 ${order.order_no}？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      }).then(async () => {
        try {
          const { data: res } = await cancelOrder({ order_id: order.id })
          if (res.code === 200 || res.code === 1 || res.code === 0) {
            this.$message.success('订单已取消')
            this.getList()
          } else {
            this.$message.error(res.message || '取消失败')
          }
        } catch (error) {
          this.$message.error('网络错误，请稍后重试')
        }
      }).catch(() => {})
    },
    deleteOrderHandle(order) {
      this.$confirm(`确定要删除订单 ${order.order_no}？删除后不可恢复！`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      }).then(async () => {
        try {
          const { data: res } = await deleteOrder({ order_id: order.id })
          if (res.code === 200 || res.code === 1 || res.code === 0) {
            this.$message.success('订单已删除')
            this.getList()
          } else {
            this.$message.error(res.message || '删除失败')
          }
        } catch (error) {
          this.$message.error('网络错误，请稍后重试')
        }
      }).catch(() => {})
    },
    confirmReceiveHandle(order) {
      this.$confirm('确认完成订单？完成后地块将变成可认领状态。', '提示', {
        confirmButtonText: '确认完成',
        cancelButtonText: '取消',
        type: 'success',
      }).then(async () => {
        try {
          const { data: res } = await farmOrderFinish({ order_id: order.id })
          if (res.code === 200 || res.code === 1 || res.code === 0) {
            this.$message.success('订单已完成，地块已释放')
            this.getList()
          } else {
            this.$message.error(res.message || '操作失败')
          }
        } catch (error) {
          this.$message.error('网络错误，请稍后重试')
        }
      }).catch(() => {})
    },
  },
}
</script>

<style scoped>
.farm-order {
  padding: 20px;
  background: #f0fdf4;
  min-height: calc(100vh - 120px);
}

.main-card {
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.08);
  border: none;
}

.header-section {
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid #dcfce7;
}

.page-title {
  font-size: 20px;
  font-weight: 600;
  color: #166534;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
}

.title-icon {
  font-size: 24px;
  color: #16a34a;
}

.content-section {
  background: #fff;
}

.search-box {
  background: linear-gradient(135deg, #f0fdf4 0%, #ffffff 100%);
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 20px;
  border: 1px solid #bbf7d0;
}

.search-row {
  margin-bottom: 16px;
}

.search-input {
  transition: all 0.3s;
}

.search-input:hover {
  box-shadow: 0 2px 8px rgba(22, 163, 74, 0.1);
}

.search-buttons {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
  align-items: center;
}

.search-buttons .el-button {
  width: 100%;
  justify-content: center;
}

.search-buttons-right {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;
  align-items: center;
}

.search-buttons-right .el-button {
  margin: 0;
  height: 28px;
  line-height: 26px;
  padding: 0 10px;
  font-size: 12px;
  min-width: auto;
  width: auto;
}

.filter-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

.filter-label {
  font-size: 14px;
  color: #166534;
  font-weight: 500;
}

.filter-radio {
  flex: 1;
}

.table-wrapper {
  background: #fff;
  border-radius: 8px;
}

.table-container {
  padding: 10px 0;
}

.order-card {
  background: #fff;
  border: 1px solid #bbf7d0;
  border-radius: 8px;
  margin-bottom: 20px;
  overflow: hidden;
  transition: all 0.3s;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.order-card:hover {
  box-shadow: 0 4px 16px rgba(22, 163, 74, 0.1);
  transform: translateY(-2px);
}

.order-card-header {
  background: linear-gradient(135deg, #ecfdf5 0%, #ffffff 100%);
  padding: 16px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #bbf7d0;
}

.order-info-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.order-no-label {
  font-size: 14px;
  color: #4b5563;
}

.order-no-value {
  font-size: 14px;
  color: #166534;
  font-weight: 500;
}

.order-info-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.create-time {
  font-size: 13px;
  color: #6b7280;
}

.order-card-body {
  padding: 16px 20px;
  background: #f0fdf4;
}

/* 收货信息样式 */
.address-section {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  margin-bottom: 16px;
  background: linear-gradient(135deg, #ecfdf5 0%, #dcfce7 100%);
  border-radius: 6px;
  border-left: 4px solid #16a34a;
}

.address-icon {
  font-size: 20px;
  color: #16a34a;
  margin-top: 2px;
}

.address-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
}

.consignee-name {
  font-size: 15px;
  font-weight: 600;
  color: #166534;
}

.consignee-phone {
  font-size: 14px;
  color: #15803d;
  margin-left: 12px;
}

.address-detail {
  font-size: 14px;
  color: #4b5563;
  line-height: 1.5;
}

/* 商品表格样式 */
.goods-table {
  border-radius: 6px;
  overflow: hidden;
}

.goods-image {
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 6px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  transition: all 0.3s;
}

.goods-image:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(22, 163, 74, 0.15);
}

.no-image {
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0fdf4;
  border-radius: 6px;
  border: 1px dashed #bbf7d0;
}

.no-image i {
  font-size: 24px;
  color: #86efac;
}

.goods-name-cell {
  font-weight: 500;
  color: #166534;
}

.price-cell {
  color: #15803d;
  font-weight: 500;
}

.quantity-cell {
  color: #4b5563;
}

.subtotal-cell {
  color: #166534;
  font-weight: 600;
}

.order-card-footer {
  padding: 16px 20px;
  background: #ffffff;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid #bbf7d0;
}

.order-summary {
  display: flex;
  align-items: center;
  gap: 24px;
}

.summary-text {
  font-size: 14px;
  color: #4b5563;
  display: flex;
  align-items: center;
  gap: 6px;
}

.highlight-text {
  color: #166534;
  font-weight: 600;
  font-size: 16px;
}

.total-amount {
  font-size: 14px;
  color: #4b5563;
}

.amount-text {
  font-size: 20px;
  font-weight: 700;
  color: #166534;
}

.order-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.action-btn {
  transition: all 0.3s;
  border-radius: 4px;
}

.action-btn:hover {
  transform: translateY(-1px);
}

.primary-btn {
  background: linear-gradient(135deg, #16a34a 0%, #15803d 100%);
  border: none;
}

.primary-btn:hover {
  background: linear-gradient(135deg, #15803d 0%, #16a34a 100%);
}

.view-btn {
  background: #dcfce7;
  color: #166534;
  border-color: #86efac;
}

.view-btn:hover {
  background: #16a34a;
  color: #fff;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
}

.empty-icon {
  font-size: 64px;
  color: #bbf7d0;
  margin-bottom: 16px;
}

.empty-text {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
}

.pagination-box {
  margin-top: 24px;
  text-align: center;
  padding: 10px 0;
}

.detail-dialog {
  border-radius: 8px;
}

.detail-content {
  padding: 10px 0;
}

.detail-table {
  margin-top: 10px;
}

.deliver-dialog {
  border-radius: 8px;
}

.deliver-form {
  padding: 10px 0;
}

.dialog-footer {
  text-align: center;
}

/* 物流轨迹样式 */
.logistics-section {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #bbf7d0;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #166534;
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.section-title i {
  color: #16a34a;
}

.logistics-timeline {
  position: relative;
  padding-left: 24px;
  max-height: 400px;
  overflow-y: auto;
}

.logistics-item {
  position: relative;
  padding-bottom: 20px;
  display: flex;
  gap: 16px;
}

.logistics-item:last-child {
  padding-bottom: 0;
}

.logistics-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #bbf7d0;
  border: 2px solid #fff;
  flex-shrink: 0;
  margin-top: 4px;
  position: relative;
  z-index: 1;
  box-shadow: 0 0 0 2px #86efac;
}

.logistics-dot.is-first {
  width: 14px;
  height: 14px;
  background: linear-gradient(135deg, #16a34a 0%, #15803d 100%);
  box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.2);
}

.logistics-item:not(:last-child)::before {
  content: '';
  position: absolute;
  left: 5px;
  top: 20px;
  bottom: 0;
  width: 2px;
  background: #bbf7d0;
}

.logistics-content {
  flex: 1;
}

.logistics-time {
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 4px;
}

.logistics-desc {
  font-size: 14px;
  color: #4b5563;
  line-height: 1.6;
}

.logistics-item:first-child .logistics-desc {
  color: #166534;
  font-weight: 500;
}

@media screen and (max-width: 1200px) {
  .search-row .el-col {
    margin-bottom: 12px;
  }
}

@media screen and (max-width: 768px) {
  .farm-order {
    padding: 12px;
  }
  
  .order-card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  
  .order-info-right {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  
  .order-card-footer {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
  
  .order-summary {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  
  .order-actions {
    width: 100%;
    justify-content: flex-start;
  }
  
  .filter-section {
    flex-direction: column;
    align-items: flex-start;
  }
}

/* 批量发货样式 */
.order-checkbox {
  margin-right: 12px;
}

.batch-ship-info {
  margin-bottom: 20px;
}

.batch-ship-form {
  padding: 10px 0;
}

.form-tip {
  font-size: 12px;
  color: #6b7280;
  margin-top: 8px;
  line-height: 1.6;
}
</style>
